import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {Button, Form, Spinner} from 'react-bootstrap';
import {useTranslation} from 'react-i18next';

const MIN_SEARCH_LENGTH = 2;
const SEARCH_DELAY = 300;

const normalizedCriteria = criteria => ({
  nickname: (criteria.nickname || '').trim(),
  name: (criteria.name || '').trim(),
  surname: (criteria.surname || '').trim()
});

export const hasPlayerSearchCriteria = criteria => Object.values(normalizedCriteria(criteria))
  .some(value => value.length >= MIN_SEARCH_LENGTH);

export const BookingPlayerSearch = ({
  partner,
  slotId,
  playersNumber,
  matchFormat,
  searchPlayers,
  clearPlayers,
  renderPlayer,
  selectedPlayer,
  onAddPlayer,
  onGuestFallback,
  showNickname = false,
  skywork = false
}) => {
  const {t} = useTranslation();
  const [criteria, setCriteria] = useState({nickname: '', name: '', surname: ''});
  const [affiliated, setAffiliated] = useState(false);
  const [status, setStatus] = useState('idle');
  const [results, setResults] = useState([]);
  const timerRef = useRef(null);
  const requestRef = useRef(0);

  const guestName = [criteria.name, criteria.surname].map(value => value.trim()).filter(Boolean).join(' ');
  const hasSelectedResult = selectedPlayer && selectedPlayer.uid !== undefined && results.some(player => Number(player.uid) === Number(selectedPlayer.uid));

  const resetResults = () => {
    requestRef.current += 1;
    setStatus('idle');
    setResults([]);
    clearPlayers();
  };

  const runSearch = async searchCriteria => {
    const normalized = normalizedCriteria(searchCriteria);
    if (!hasPlayerSearchCriteria(normalized)) {
      resetResults();
      return;
    }

    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setStatus('loading');
    let response;
    try {
      response = await searchPlayers(
        normalized.nickname,
        normalized.name,
        normalized.surname,
        affiliated,
        slotId,
        partner.uid,
        playersNumber,
        matchFormat
      );
    } catch (error) {
      response = {status: 500, error};
    }
    if (requestRef.current !== requestId) return;

    if (response && response.status === 200) {
      setResults(Array.isArray(response.players) ? response.players : []);
      setStatus('success');
    } else {
      setResults([]);
      setStatus('error');
    }
  };

  useEffect(() => {
    window.clearTimeout(timerRef.current);
    if (!hasPlayerSearchCriteria(criteria)) {
      resetResults();
      return undefined;
    }
    timerRef.current = window.setTimeout(() => runSearch(criteria), SEARCH_DELAY);
    return () => window.clearTimeout(timerRef.current);
    // Search callbacks are stable Redux bindings; criteria and affiliation drive requests.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criteria.nickname, criteria.name, criteria.surname, affiliated]);

  useEffect(() => () => {
    window.clearTimeout(timerRef.current);
    requestRef.current += 1;
    clearPlayers();
  }, [clearPlayers]);

  const updateCriteria = event => {
    const {name, value} = event.target;
    requestRef.current += 1;
    setCriteria(current => ({...current, [name]: value}));
  };

  const searchNow = () => {
    window.clearTimeout(timerRef.current);
    runSearch(criteria);
  };

  return (
    <div className={skywork ? 'skywork-booking-player-search' : ''}>
      <p className="skywork-search-help">{t('BOOKING_PLAYER_SEARCH_HELP')}</p>
      <Form.Group className={skywork ? 'skywork-player-search-fields' : ''} style={!skywork ? {display: 'flex', flexDirection: 'row'} : null}>
        {showNickname ? <Form.Control name="nickname" style={!skywork ? {maxWidth: 150} : null} placeholder={t('NICKNAME')} type="text" maxLength="25" value={criteria.nickname} onChange={updateCriteria}/> : null}
        <Form.Control name="name" style={!skywork ? {maxWidth: 150} : null} placeholder={t('NAME')} type="text" maxLength="25" value={criteria.name} onChange={updateCriteria}/>
        <Form.Control name="surname" style={!skywork ? {maxWidth: 150} : null} placeholder={t('SURNAME')} type="text" maxLength="25" value={criteria.surname} onChange={updateCriteria}/>
      </Form.Group>
      <Form.Group className={skywork ? 'skywork-player-search-actions' : ''} style={!skywork ? {display: 'flex', justifyContent: 'space-between'} : null}>
        <Form.Check name="playerAffiliate" type="checkbox" label={t('PLAYER_AFFILIATE', {partner: partner.name})} checked={affiliated} onChange={() => setAffiliated(current => !current)}/>
        <Button type="button" disabled={!hasPlayerSearchCriteria(criteria) || status === 'loading'} variant="secondary" onClick={searchNow}>{t('SEARCH')}</Button>
      </Form.Group>
      <div className={skywork ? 'skywork-player-search-results' : ''} aria-live="polite">
        {status === 'loading' ? <div className="booking-player-search-status"><Spinner animation="border" size="sm"/><span>{t('BOOKING_PLAYER_SEARCH_LOADING')}</span></div> : null}
        {status === 'error' ? <div className="booking-player-search-status booking-player-search-status--error">{t('BOOKING_PLAYER_SEARCH_ERROR')}</div> : null}
        {status === 'success' && results.length === 0 ? (
          <div className="booking-player-search-empty">
            <strong>{t('BOOKING_PLAYER_NOT_FOUND')}</strong>
            {guestName && onGuestFallback ? <Button type="button" variant="outline-primary" onClick={() => onGuestFallback({name: criteria.name.trim(), surname: criteria.surname.trim()})}>{t('BOOKING_ADD_TYPED_GUEST', {name: guestName})}</Button> : null}
          </div>
        ) : null}
        {results.map(player => renderPlayer(player, affiliated))}
        {results.length > 0 ? <Button type="button" className={skywork ? 'skywork-primary-action' : ''} style={!skywork ? {maxWidth: 100} : null} disabled={!hasSelectedResult} variant="secondary" onClick={onAddPlayer}>{skywork ? t('SKYWORK_ADD_TO_BOOKING') : t('SELECT')}</Button> : null}
      </div>
    </div>
  );
};

BookingPlayerSearch.propTypes = {
  partner: PropTypes.shape({uid: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), name: PropTypes.string}).isRequired,
  slotId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  playersNumber: PropTypes.number,
  matchFormat: PropTypes.oneOf(['singles', 'doubles']),
  searchPlayers: PropTypes.func.isRequired,
  clearPlayers: PropTypes.func.isRequired,
  renderPlayer: PropTypes.func.isRequired,
  selectedPlayer: PropTypes.object,
  onAddPlayer: PropTypes.func.isRequired,
  onGuestFallback: PropTypes.func,
  showNickname: PropTypes.bool,
  skywork: PropTypes.bool
};

BookingPlayerSearch.defaultProps = {
  selectedPlayer: {},
  onGuestFallback: null,
  playersNumber: null,
  matchFormat: null
};
